import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Copy, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferFriend() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/referrals`, { email: email.trim() });
      setResult(data);
      toast.success(data.email_sent ? "Your link is ready — we also emailed it to you." : "Your link is ready.");
    } catch {
      toast.error("Could not create your link. Please check the email address and try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(result.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="refer" data-testid="refer-section" className="border-t border-linen bg-forest text-bone">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="lg:col-span-6"
          >
            <p className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.35em] text-terra">
              <Gift size={14} /> Refer a friend
            </p>
            <h2 className="mb-5 font-serif text-4xl font-medium leading-tight tracking-tight md:text-5xl" data-testid="refer-heading">
              Give 10%. <span className="italic text-terra">Get $50.</span>
            </h2>
            <p className="max-w-md text-sm leading-relaxed text-bone/70">
              Share your personal link — every friend gets <strong className="text-bone">10% off their first paid
              service</strong>, and you earn <strong className="text-bone">$50 off your next one</strong> each time a
              friend books a consultation or completes a payment. No limit.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
            className="flex flex-col justify-center lg:col-span-6"
          >
            {result ? (
              <div className="border border-bone/15 p-7" data-testid="refer-result">
                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">
                  Your link{result.email_sent ? " — also sent to your inbox" : ""}
                </p>
                <div className="flex items-center gap-3">
                  <p className="min-w-0 flex-1 truncate font-mono text-sm text-terra" data-testid="refer-link">{result.link}</p>
                  <button
                    onClick={copy}
                    data-testid="refer-copy-btn"
                    className="flex shrink-0 items-center gap-2 border border-bone/25 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/80 transition-colors duration-300 hover:border-terra hover:text-terra"
                  >
                    {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <p className="mt-4 text-xs text-bone/50">
                  Your code: <span className="font-mono text-terra">{result.code}</span> — share it anywhere.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-bone/15 p-7" data-testid="refer-form">
                <label className="mb-3 block font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50" htmlFor="refer-email">
                  Enter your email to get your link
                </label>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    id="refer-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    data-testid="refer-email-input"
                    className="min-w-0 flex-1 border-b border-bone/30 bg-transparent py-3 text-sm text-bone placeholder:text-bone/30 focus:border-terra focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="refer-submit-btn"
                    className="flex shrink-0 items-center justify-center gap-2 border border-terra bg-terra px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-terra disabled:opacity-60"
                  >
                    {loading && <Loader2 size={13} className="animate-spin" />} Get my link
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
